/**
 * Chart Block Renderer — chat path adapter.
 *
 * On the chat path, `block.content` for chart tools arrives in the canonical
 * shape declared by `packages/workflow-apps/src/apps/ui/shared/chart_schemas.ts`
 * — the single source of truth for what the LLM emits. That wire shape
 * differs from the legacy dynamic-key shape that `@strivelabs/ui-blocks`
 * chart wrappers (and existing demo/report consumers in this app) accept.
 *
 * This file is the one boundary on the client where the wire shape is
 * adapted to the ui-blocks shape. The wire types live in
 * `chart-wire-types.ts`; the pure adapter helpers in
 * `chart-wire-adapters.ts`. This file is just the React layer that
 * memoizes the adapter calls and forwards to the ui-blocks wrapper.
 *
 * Regression-lock strategy: the wire types in `chart-wire-types.ts` are
 * mirrored structurally by the Zod schemas in `chart_schemas.ts`.
 * `schema_renderer_contract.test.ts` asserts
 * `z.infer<typeof <chart>InputSchema>` satisfies a matching reference type,
 * so a schema-or-renderer drift fails CI.
 */

import React, { useMemo } from 'react';
import {
  LineChart,
  BarChart,
  AreaChart,
  PieChart,
  RadialChart,
  ScatterChart,
  TabbedChart,
} from '@strivelabs/ui-blocks';
import type { TabbedChartTab } from '@strivelabs/ui-blocks';
import type { UIBlock } from '@/types/ui-blocks';
import type {
  LineChartWireContent,
  BarChartWireContent,
  AreaChartWireContent,
  PieChartWireContent,
  RadialChartWireContent,
  ScatterChartWireContent,
  TabbedChartWireContent,
} from './chart-wire-types';
import {
  adaptLineWireToProps,
  adaptBarWireToProps,
  adaptAreaWireToProps,
  adaptPieWireToProps,
  adaptRadialWireToProps,
  adaptScatterWireToProps,
} from './chart-wire-adapters';

// ---------------------------------------------------------------------------
// Entry — dispatches the standalone chart block types
// ---------------------------------------------------------------------------

export interface ChartBlockRendererProps {
  block: UIBlock;
}

/**
 * Back-compat shim — detects stored content that's in the LEGACY ui-blocks
 * chart shape (axis-based: `xAxisKey` present; slice-based: `nameKey`/`dataKey`
 * present). When the LLM emits the new wire shape, neither of those fields is
 * present. The detector lets old insight reports (already stored under the
 * OLD shape via `insights_processor`) render via the legacy ui-blocks wrapper
 * directly while new content flows through the wire-shape adapter.
 */
function isLegacyAxisChartShape(content: unknown): boolean {
  if (!content || typeof content !== 'object') return false;
  return typeof (content as { xAxisKey?: unknown }).xAxisKey === 'string';
}

function isLegacySliceChartShape(content: unknown): boolean {
  if (!content || typeof content !== 'object') return false;
  const obj = content as { nameKey?: unknown; dataKey?: unknown };
  return typeof obj.nameKey === 'string' || typeof obj.dataKey === 'string';
}

export function ChartBlockRenderer({ block }: ChartBlockRendererProps) {
  const content = (block as { content?: unknown }).content;

  if (block.type === 'line_chart') {
    if (isLegacyAxisChartShape(content)) {
      return <LineChart {...(content as React.ComponentProps<typeof LineChart>)} />;
    }
    return <LineChartRenderer content={content as LineChartWireContent} />;
  }

  if (block.type === 'bar_chart') {
    if (isLegacyAxisChartShape(content)) {
      return <BarChart {...(content as React.ComponentProps<typeof BarChart>)} />;
    }
    return <BarChartRenderer content={content as BarChartWireContent} />;
  }

  if (block.type === 'area_chart') {
    if (isLegacyAxisChartShape(content)) {
      return <AreaChart {...(content as React.ComponentProps<typeof AreaChart>)} />;
    }
    return <AreaChartRenderer content={content as AreaChartWireContent} />;
  }

  if (block.type === 'pie_chart') {
    if (isLegacySliceChartShape(content)) {
      return <PieChart {...(content as React.ComponentProps<typeof PieChart>)} />;
    }
    return <PieChartRenderer content={content as PieChartWireContent} />;
  }

  if (block.type === 'radial_chart') {
    if (isLegacySliceChartShape(content)) {
      return <RadialChart {...(content as React.ComponentProps<typeof RadialChart>)} />;
    }
    return <RadialChartRenderer content={content as RadialChartWireContent} />;
  }

  if (block.type === 'scatter_chart') {
    if (isLegacyAxisChartShape(content)) {
      return <ScatterChart {...(content as React.ComponentProps<typeof ScatterChart>)} />;
    }
    return <ScatterChartRenderer content={content as ScatterChartWireContent} />;
  }

  return null;
}

// ---------------------------------------------------------------------------
// Per-chart renderers (memoize the adapter call; layer wrapper-owned props)
// ---------------------------------------------------------------------------

function LineChartRenderer({ content }: { content: LineChartWireContent }) {
  const props = useMemo(() => adaptLineWireToProps(content), [content]);
  return (
    <LineChart
      {...props}
      title={content.title ?? undefined}
      description={content.description ?? undefined}
    />
  );
}

function BarChartRenderer({ content }: { content: BarChartWireContent }) {
  const props = useMemo(() => adaptBarWireToProps(content), [content]);
  return (
    <BarChart
      {...props}
      title={content.title ?? undefined}
    />
  );
}

function AreaChartRenderer({ content }: { content: AreaChartWireContent }) {
  const props = useMemo(() => adaptAreaWireToProps(content), [content]);
  return (
    <AreaChart
      {...props}
      title={content.title ?? undefined}
      description={content.description ?? undefined}
    />
  );
}

function PieChartRenderer({ content }: { content: PieChartWireContent }) {
  const props = useMemo(() => adaptPieWireToProps(content), [content]);
  return (
    <PieChart
      {...props}
      title={content.title ?? undefined}
      description={content.description ?? undefined}
    />
  );
}

function RadialChartRenderer({ content }: { content: RadialChartWireContent }) {
  const props = useMemo(() => adaptRadialWireToProps(content), [content]);
  return (
    <RadialChart
      {...props}
      title={content.title ?? undefined}
      description={content.description ?? undefined}
    />
  );
}

function ScatterChartRenderer({ content }: { content: ScatterChartWireContent }) {
  const props = useMemo(() => adaptScatterWireToProps(content), [content]);
  return (
    <ScatterChart
      {...props}
      title={content.title ?? undefined}
      description={content.description ?? undefined}
    />
  );
}

// ---------------------------------------------------------------------------
// Tabbed-chart renderer
//
// Builds the discriminated-union `tabs` array that `TabbedChart` expects by
// calling the same per-chart adapters used by the standalone renderers above.
// Without this layer, `TabbedChart` would receive the wire-shape `chartProps`
// it can't consume, and every `tabbed_chart` block would render broken.
// ---------------------------------------------------------------------------

export interface TabbedChartBlockRendererProps {
  block: { content: TabbedChartWireContent };
}

/**
 * Detects whether a tabbed chart payload is in the LEGACY ui-blocks shape
 * (each `tabs[i].chartProps` carries `xAxisKey` for axis charts or
 * `nameKey`/`dataKey` for slice charts). LLMs emit all tabs in the same
 * shape, so sampling `tabs[0]` is sufficient.
 */
function isLegacyTabbedChartShape(content: { tabs?: unknown }): boolean {
  if (!Array.isArray(content.tabs) || content.tabs.length === 0) return false;
  const first = content.tabs[0] as { chartType?: string; chartProps?: unknown } | null;
  if (!first || typeof first !== 'object') return false;
  const sliceTypes = new Set(['pie', 'radial']);
  if (sliceTypes.has(first.chartType ?? '')) {
    return isLegacySliceChartShape(first.chartProps);
  }
  return isLegacyAxisChartShape(first.chartProps);
}

export function TabbedChartBlockRenderer({ block }: TabbedChartBlockRendererProps) {
  const { content } = block;
  // Back-compat: stored reports / chat history may still carry the legacy
  // ui-blocks `chartProps` shape. The detector samples tabs[0] (LLMs emit all
  // tabs in one shape, so this is enough). Hook order is preserved: useMemo
  // still runs but returns [] for the legacy path.
  const isLegacy = isLegacyTabbedChartShape(content);

  const tabs = useMemo<TabbedChartTab[]>(() => {
    if (isLegacy) return [];
    return content.tabs.map((tab, i) => {
      const id = tab.id ?? `tab-${i}`;
      switch (tab.chartType) {
        case 'line':
          return { id, label: tab.label, chartType: 'line', chartProps: adaptLineWireToProps(tab.chartProps) };
        case 'bar':
          return { id, label: tab.label, chartType: 'bar', chartProps: adaptBarWireToProps(tab.chartProps) };
        case 'area':
          return { id, label: tab.label, chartType: 'area', chartProps: adaptAreaWireToProps(tab.chartProps) };
        case 'pie':
          return { id, label: tab.label, chartType: 'pie', chartProps: adaptPieWireToProps(tab.chartProps) };
        case 'radial':
          return { id, label: tab.label, chartType: 'radial', chartProps: adaptRadialWireToProps(tab.chartProps) };
      }
    });
  }, [content.tabs, isLegacy]);

  if (isLegacy) {
    return <TabbedChart {...(content as unknown as React.ComponentProps<typeof TabbedChart>)} />;
  }

  return (
    <TabbedChart
      tabs={tabs}
      title={content.title ?? undefined}
      description={content.description ?? undefined}
      defaultTabId={content.defaultTabId ?? undefined}
      height={content.height ?? undefined}
      referenceLines={content.referenceLines ?? undefined}
      referenceDots={content.referenceDots ?? undefined}
      referenceAreas={content.referenceAreas ?? undefined}
    />
  );
}
